import {
	CommercialSuretyBondType,
	formatFileNames,
	isCommercialSuretyBondType,
} from "@inrev/common";
import { DateTime } from "luxon";
import { type FC, type ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { formatBondAmount, formatEmpty } from "../../../../utils";
import { Button, Card, Text } from "../../../primitives";
import { useBondRequestSubmissionAdapter } from "../BondRequestSubmissionAdapterContext";
import type { SubmissionFormData } from "../bondRequestSubmissionFormSchema";

type SummaryStepProps = {
	onBack: () => void;
	onSubmit: () => void;
};

type SummaryRowProps = {
	label: string;
	value: ReactNode;
	testId?: string;
};

const SummaryRow: FC<SummaryRowProps> = ({ label, value, testId }) => (
	<div className="flex items-start gap-2 w-full" data-testid={testId}>
		<Text type="body" color="secondary" className="shrink-0 w-[200px]">
			{label}
		</Text>
		<Text type="body" color="primary" className="min-w-0 flex-1">
			{value}
		</Text>
	</div>
);

const formatEffectiveDate = (effectiveDate: string | undefined) => {
	if (!effectiveDate) return formatEmpty(undefined);
	const parsed = DateTime.fromISO(effectiveDate, { zone: "utc" });
	return parsed.isValid ? parsed.toFormat("MM/dd/yyyy") : formatEmpty(undefined);
};

const bondTypeLabel = (bondType: SubmissionFormData["bondType"]) => {
	if (isCommercialSuretyBondType(bondType)) {
		return CommercialSuretyBondType.label(bondType);
	}
	return formatEmpty(undefined);
};

export const SummaryStep: FC<SummaryStepProps> = ({ onBack, onSubmit }) => {
	const { control } = useFormContext<SubmissionFormData>();
	const principal = useWatch({ control, name: "principal" });
	const bondType = useWatch({ control, name: "bondType" });
	const bondAmount = useWatch({ control, name: "bondAmount" });
	const effectiveDate = useWatch({ control, name: "effectiveDate" });
	const submissionNarrative = useWatch({ control, name: "submissionNarrative" });
	const bondForm = useWatch({ control, name: "bondForm" });

	const { companies } = useBondRequestSubmissionAdapter();
	const principalName = companies?.find((company) => company.id === principal)?.name;
	const attachmentsLabel = formatEmpty(formatFileNames(bondForm?.upload?.files));

	return (
		<div className="flex flex-col gap-5 px-6 pt-6 pb-6" data-testid="commercial-summary-step">
			<Card elevation="raised">
				<Card.Header className="px-5 py-3.5">
					<Card.Title className="text-sm font-semibold">Bond</Card.Title>
				</Card.Header>
				<Card.Body className="flex flex-col gap-2.5 px-5 py-3.5">
					<SummaryRow
						label="Principal"
						value={formatEmpty(principalName)}
						testId="summary-principal"
					/>
					<SummaryRow
						label="Bond Type"
						value={bondTypeLabel(bondType)}
						testId="summary-bond-type"
					/>
					<SummaryRow
						label="Bond Amount"
						value={formatBondAmount(bondAmount)}
						testId="summary-bond-amount"
					/>
					<SummaryRow
						label="Effective Date"
						value={formatEffectiveDate(effectiveDate)}
						testId="summary-effective-date"
					/>
				</Card.Body>
			</Card>

			<Card elevation="raised">
				<Card.Header className="px-5 py-3.5">
					<Card.Title className="text-sm font-semibold">Additional Info</Card.Title>
				</Card.Header>
				<Card.Body className="flex flex-col gap-2.5 px-5 py-3.5">
					<SummaryRow
						label="Submission Narrative"
						value={
							submissionNarrative?.trim() ? (
								<span className="line-clamp-6 whitespace-pre-wrap">{submissionNarrative}</span>
							) : (
								formatEmpty(undefined)
							)
						}
						testId="summary-submission-narrative"
					/>
					<SummaryRow label="Attachments" value={attachmentsLabel} testId="summary-attachments" />
				</Card.Body>
			</Card>

			<div className="flex justify-end gap-2 border-t border-bc-default pt-3">
				<Button
					color="secondary"
					variant="solid"
					onClick={onBack}
					data-testid="commercial-summary-back-btn"
					trackingId="bond-request-modal-commercial-summary-back"
				>
					Back
				</Button>
				<Button
					color="primary"
					onClick={onSubmit}
					data-testid="commercial-summary-submit-btn"
					trackingId="bond-request-modal-commercial-summary-submit"
				>
					Submit
				</Button>
			</div>
		</div>
	);
};
